// @flow

import * as React from 'react';
import Box from 'sail/Box';
import {CaptionCaps} from 'sail/Text';
import {useVideos, useArticle} from 'docs/data';
import type {VideosContextType, ArticleVideoModel} from 'docs/data';
import {CodeBlock} from '../CodeBlock/CodeBlock';

type VideoTestRequestProps = {
  videoId?: string,
  topicId?: string,
  playlistId?: string,
};

type VideoTestProps = {|
  showAll?: boolean,
  id?: string,
  className?: string,
  children?: React.Node,
|} & VideoTestRequestProps;

const getDemoInfo = (
  videos: VideosContextType,
  props: VideoTestRequestProps,
) => {
  const {videoId, topicId, playlistId} = props;

  if (videoId) {
    return [videos.getVideo(videoId), `videos.getVideo("${videoId}")`];
  } else if (topicId) {
    return [videos.getTopic(topicId), `videos.getTopic("${topicId}")`];
  } else if (playlistId) {
    return [
      videos.getPlaylist(playlistId),
      `videos.getPlaylist("${playlistId}")`,
    ];
  } else {
    return [];
  }
};

const getArticleFrontmatter = (videos: ?ArticleVideoModel): string => {
  if (!videos) {
    return '';
  } else if (videos.topic) {
    return `topics/${videos.topic.id}`;
  } else if (videos.video) {
    return `videos/${videos.video.id}`;
  } else {
    return '';
  }
};

export const VideoTest = (props: VideoTestProps) => {
  // NOTE: this is just using the any type because this is a test
  // usually the request you pass in determine the response type
  const videos = useVideos({prefetchAllVideos: true});
  const [data, exampleString] = getDemoInfo(videos, props);

  const {article} = useArticle();

  return (
    <Box>
      {article.videos && (
        <>
          <CaptionCaps>Article Frontmatter</CaptionCaps>
          <CodeBlock
            numbered={false}
            language="yaml"
          >{`---\nvideo: "${getArticleFrontmatter(
            article.videos,
          )}"\n---`}</CodeBlock>
          <CaptionCaps>Article Video</CaptionCaps>
          <CodeBlock numbered={false} language="json">
            {JSON.stringify(article.videos || {}, null, 2)}
          </CodeBlock>
        </>
      )}

      {props.showAll && (
        <>
          <CaptionCaps>Request</CaptionCaps>
          <CodeBlock numbered={false} language="js">
            {`const videos = useVideos();\nconst data = ${exampleString};`}
          </CodeBlock>

          <CaptionCaps>Response</CaptionCaps>
          <CodeBlock numbered={false} language="json">
            {JSON.stringify(data, null, 2)}
          </CodeBlock>
          <CaptionCaps>All Videos</CaptionCaps>
          <CodeBlock numbered={false} language="json">
            {JSON.stringify(videos.getAll(), null, 2)}
          </CodeBlock>
        </>
      )}
    </Box>
  );
};
